/**
 * Show-widget fence parser.
 *
 * Extracts `show-widget` code fences from assistant message content,
 * returning alternating text/widget segments for rendering.
 */

export interface ShowWidgetData {
  title?: string;
  widget_code: string;
}

export type WidgetSegment =
  | { type: 'text'; content: string }
  | { type: 'widget'; data: ShowWidgetData };

/** Parse ALL show-widget fences in text, returning alternating text/widget segments. */
/**
 * 找到从 start(必须指向 `{`)开始的 JSON 对象结束位置(闭合 `}` 的下一个下标)。
 * 字符串感知:忽略引号内的 `{}`,并正确跳过 `\"` 转义;未闭合(流式截断)返回 -1。
 */
function scanJsonObjectEnd(s: string, start: number): number {
  let depth = 0;
  let inStr = false;
  let esc = false;
  for (let i = start; i < s.length; i++) {
    const c = s[i];
    if (inStr) {
      if (esc) { esc = false; continue; }
      if (c === '\\') { esc = true; continue; }
      if (c === '"') inStr = false;
      continue;
    }
    if (c === '"') { inStr = true; continue; }
    if (c === '{') depth++;
    else if (c === '}') { depth--; if (depth === 0) return i + 1; }
  }
  return -1;
}

/**
 * 把模型输出里各种畸形的 show-widget 写法归一化成标准闭合围栏,喂给现有 parser:
 *   - ```show-widget 开头 + `</show-widget>` 伪 XML 结尾 → 标准 ``` 闭合(真实高频畸形)
 *   - 裸 `show-widget {…}` 指令(无围栏) → 包成闭合围栏;尾随 `</show-widget>`/`</widget>` 吞掉
 *   - JSON 截断(流式中) → 开放围栏,驱动 partial-render
 * 标准围栏 byte-for-byte 原样保留(幂等);纯文本里提及 "show-widget" 不改写。
 */
export function normalizeWidgetFences(text: string): string {
  if (!text.includes('show-widget')) return text;
  // DeepSeek 系模型有时把自家 DSML 函数调用标记当文本吐出来包在 widget 外面：
  //   <｜DSML｜tool_calls><｜DSML｜invoke name="show-widget"><｜DSML｜parameter name="…">{…}
  //   </｜DSML｜parameter></｜DSML｜invoke></｜DSML｜tool_calls>   （｜= U+FF5C，单/双均见）
  // 上游常剥掉开标签却漏下闭合标签，结果正文里冒出一串 </｜DSML｜…> 残渣。这些标记对
  // 用户毫无意义，整体剥掉，只把里面的 JSON 交给下面的围栏归一化。
  text = text.replace(/<\/?\uFF5C+DSML\uFF5C+[^>]*>/g, '');
  const TOKEN = 'show-widget';
  let out = '';
  let i = 0;
  while (i < text.length) {
    const idx = text.indexOf(TOKEN, i);
    if (idx === -1) { out += text.slice(i); break; }
    const fenced = idx >= 3 && text.startsWith('```', idx - 3);
    // 裸指令仅在词首独立出现时成立(排除 </show-widget> 子串、prose 中粘连)；
    // 另认 `<show-widget> {…}` 这种伪 XML 开标签写法(真实出现过：模型把指令当标签发)
    const tagOpen = !fenced && idx >= 1 && text[idx - 1] === '<';
    const bareOk = !fenced && (idx === 0 || /\s/.test(text[idx - 1]) || tagOpen);
    // 指令后只允许空白(开标签形式还允许一个 '>')，然后必须是 JSON 对象起点
    let j = idx + TOKEN.length;
    while (j < text.length && /\s/.test(text[j])) j++;
    if (tagOpen && text[j] === '>') { j++; while (j < text.length && /\s/.test(text[j])) j++; }
    const hasObj = text[j] === '{';
    if (!fenced && (!bareOk || !hasObj)) {
      // 普通文字里的提及:原样保留
      out += text.slice(i, idx + TOKEN.length);
      i = idx + TOKEN.length;
      continue;
    }
    if (fenced && !hasObj) {
      // 围栏后没有对象(空围栏/流式刚起步):不动,交给现有 parser 的截断兜底
      out += text.slice(i);
      break;
    }
    const objEnd = scanJsonObjectEnd(text, j);
    out += text.slice(i, fenced ? idx - 3 : (tagOpen ? idx - 1 : idx));
    if (objEnd === -1) {
      // JSON 截断(流式中):开放围栏,partial-render 可先画
      out += '```show-widget\n' + text.slice(j);
      break;
    }
    const obj = text.slice(j, objEnd);
    let k = objEnd;
    while (k < text.length && /\s/.test(text[k])) k++;
    if (fenced && text.startsWith('```', k)) {
      // 标准闭合围栏:整段原样保留(byte-for-byte 幂等)
      out += text.slice(idx - 3, k + 3);
      i = k + 3;
      continue;
    }
    out += '```show-widget\n' + obj + '\n```';
    if (text.startsWith('</show-widget>', k)) i = k + '</show-widget>'.length;
    else if (text.startsWith('</widget>', k)) i = k + '</widget>'.length;
    else i = objEnd; // 无伪闭合:对象后的原文(含空白)原样接在闭合围栏后
  }
  return out;
}

/** Parse ALL show-widget fences in text, returning alternating text/widget segments. */
export function parseAllShowWidgets(rawText: string): WidgetSegment[] {
  // 先归一化畸形围栏(伪 XML 闭合/裸指令/截断),再走标准围栏解析
  const text = normalizeWidgetFences(rawText);
  const segments: WidgetSegment[] = [];
  const fenceRegex = /```show-widget\s*\n?([\s\S]*?)\n?\s*```/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let foundAny = false;

  while ((match = fenceRegex.exec(text)) !== null) {
    foundAny = true;
    // Text before this fence
    const before = text.slice(lastIndex, match.index).trim();
    if (before) segments.push({ type: 'text', content: before });

    // Parse widget JSON
    let parsed = false;
    try {
      const json = JSON.parse(match[1]);
      if (json.widget_code) {
        segments.push({ type: 'widget', data: { title: json.title || undefined, widget_code: String(json.widget_code) } });
        parsed = true;
      }
    } catch { /* fall through to lenient repair */ }
    if (!parsed) {
      // 模型偶发在 widget_code 字符串里写未转义的 ASCII 引号(如中文行文里的 "上游资源")，
      // 严格 JSON.parse 在第一个裸引号处就断。此前这里静默丢弃整段，用户看到的是
      // "图谱消失"。改用与流式截断同一套字符串定位抽取(定位 "widget_code":" 后
      // 取到末尾的 "} 再反转义)，能把 SVG 整体救回；实在救不回也退回可见的代码块，
      // 绝不再无声吞掉内容。
      const repaired = extractTruncatedWidget(match[1]);
      if (repaired) segments.push({ type: 'widget', data: repaired });
      else segments.push({ type: 'text', content: '```json\n' + match[1] + '\n```' });
    }

    lastIndex = match.index + match[0].length;
  }

  if (!foundAny) {
    // Fallback: handle truncated output (last fence not closed)
    const fenceStart = text.indexOf('```show-widget');
    if (fenceStart === -1) return [];

    const before = text.slice(0, fenceStart).trim();
    if (before) segments.push({ type: 'text', content: before });

    const fenceBody = text.slice(fenceStart + '```show-widget'.length).trim();
    const widget = extractTruncatedWidget(fenceBody);
    if (widget) segments.push({ type: 'widget', data: widget });
    return segments;
  }

  // Remaining text after last fence
  const remaining = text.slice(lastIndex).trim();
  if (remaining) {
    // Check if remaining text has a truncated widget fence
    const truncFenceStart = remaining.indexOf('```show-widget');
    if (truncFenceStart !== -1) {
      const beforeTrunc = remaining.slice(0, truncFenceStart).trim();
      if (beforeTrunc) segments.push({ type: 'text', content: beforeTrunc });
      const truncBody = remaining.slice(truncFenceStart + '```show-widget'.length).trim();
      const widget = extractTruncatedWidget(truncBody);
      if (widget) segments.push({ type: 'widget', data: widget });
    } else {
      segments.push({ type: 'text', content: remaining });
    }
  }

  return segments;
}

/**
 * Compute the React key for a partial (still-streaming) widget so that it
 * matches the key it will receive once its fence closes and the full content
 * is parsed by parseAllShowWidgets.
 *
 * If these keys diverge, React will unmount + remount the WidgetRenderer
 * → iframe destroyed → height collapse → scroll jump.
 */
export function computePartialWidgetKey(content: string): string {
  const lastFenceStart = content.lastIndexOf('```show-widget');
  const beforePart = content.slice(0, lastFenceStart).trim();
  const hasCompletedFences = beforePart.length > 0 && /```show-widget/.test(beforePart);
  const completedSegments = hasCompletedFences ? parseAllShowWidgets(beforePart) : [];
  return `w-${hasCompletedFences ? completedSegments.length : (beforePart ? 1 : 0)}`;
}

/** Extract widget_code from truncated/incomplete JSON (no closing fence). */
export function extractTruncatedWidget(fenceBody: string): ShowWidgetData | null {
  // Try full JSON parse first
  try {
    const json = JSON.parse(fenceBody);
    if (json.widget_code) return { title: json.title || undefined, widget_code: String(json.widget_code) };
  } catch { /* expected — JSON is truncated */ }

  // String-search extraction
  const keyIdx = fenceBody.indexOf('"widget_code"');
  if (keyIdx === -1) return null;
  const colonIdx = fenceBody.indexOf(':', keyIdx + 13);
  if (colonIdx === -1) return null;
  const quoteIdx = fenceBody.indexOf('"', colonIdx + 1);
  if (quoteIdx === -1) return null;

  let raw = fenceBody.slice(quoteIdx + 1);
  raw = raw.replace(/"\s*\}\s*$/, '');
  if (raw.endsWith('\\')) raw = raw.slice(0, -1);
  try {
    const widgetCode = raw
      .replace(/\\\\/g, '\x00BACKSLASH\x00')
      .replace(/\\n/g, '\n')
      .replace(/\\t/g, '\t')
      .replace(/\\r/g, '\r')
      .replace(/\\"/g, '"')
      .replace(/\x00BACKSLASH\x00/g, '\\');
    if (widgetCode.length < 10) return null;

    let title: string | undefined;
    const titleMatch = fenceBody.match(/"title"\s*:\s*"([^"]*?)"/);
    if (titleMatch) title = titleMatch[1];
    return { title, widget_code: widgetCode };
  } catch {
    return null;
  }
}
